import { forwardRef, useState } from "react";
import { margin } from "styled-system";
import styled, { css, keyframes } from "styled-components";
import cx from "classnames";
import Motion from "rc-motion";
import { CloseOutlined, CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, CloseCircleOutlined, CheckCircleFilled, ExclamationCircleFilled, InfoCircleFilled, CloseCircleFilled } from "../../icons";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const slideUp = keyframes(["0%{transform:scaleY(1);transform-origin:0 0;opacity:1;}100%{transform:scaleY(0);transform-origin:0 0;opacity:0;}"]);
const PrefixIcon = styled.span.withConfig({
  displayName: "alert__PrefixIcon",
  componentId: "sc-9h3n6-0"
})(["position:absolute;top:11.5px;left:", "px;.anticon{position:inherit;}"], p => p.theme.space.md);
const Message = styled.span.withConfig({
  displayName: "alert__Message",
  componentId: "sc-9h3n6-1"
})(["color:", ";"], p => p.theme.colors.heading);
const Description = styled.span.withConfig({
  displayName: "alert__Description",
  componentId: "sc-9h3n6-2"
})(["display:block;font-size:", "px;line-height:22px;"], p => p.theme.fontSizes.md);
const CloseButton = styled.button.withConfig({
  displayName: "alert__CloseButton",
  componentId: "sc-9h3n6-3"
})(["position:absolute;top:8px;right:8px;padding:0;font-size:", "px;line-height:22px;width:22px;height:22px;background-color:transparent;transition:background-color 0.3s;border-radius:50%;border:none;outline:none;overflow:hidden;cursor:pointer;"], p => p.theme.fontSizes.sm);
const makeTypeColor = (type, color) => p => p.type === type && css(["background-color:", ";border:2px solid ", ";", "{color:", ";}", ",", ",", " .anticon{color:", ";}", ":hover{background-color:", ";}"], p.theme.colors[color][0], p.theme.colors[color][2], PrefixIcon, p.theme.colors[color].base, Message, Description, CloseButton, p.theme.colors[color][6], CloseButton, p.theme.colors[color][1]);
const StyledAlert = styled.div.withConfig({
  displayName: "alert__StyledAlert",
  componentId: "sc-9h3n6-4"
})(["position:relative;padding:8px 16px 8px 38px;word-wrap:break-word;border-radius:", "px;", " &.ant-alert-motion-leave{animation-fill-mode:both;animation:", ";}&.ant-alert-motion-leave-active{transform:scaleY(0);transform-origin:0 0;opacity:0;}", " ", " ", " ", " ", " ", " ", " ", ""], p => p.theme.radii.base, margin, p => css(["", " ", " ", ""], slideUp, p.theme.animationDurations.slow, p.theme.transitions.easeInOutCircle), makeTypeColor("info", "info"), makeTypeColor("success", "positive"), makeTypeColor("error", "negative"), makeTypeColor("warning", "warning"), p => p.description && css(["position:relative;padding:16px 16px 16px 56px;color:", ";line-height:", ";", "{display:block;margin-bottom:4px;font-size:", "px;}", "{position:absolute;top:", "px;left:", "px;font-size:", "px;}"], p.theme.colors.text, p.theme.lineHeights.base, Message, p.theme.fontSizes.lg, PrefixIcon, p.theme.space.md, p.theme.space.md, p.theme.fontSizes[5]), p => p.banner && css(["border:0;border-radius:0;"]), p => !p.showIcon && css(["padding:", ";"], p.description ? "16px" : "8px 16px"), p => p.closable && css(["padding-right:30px;"]));
const iconMapFilled = {
  success: CheckCircleFilled,
  info: InfoCircleFilled,
  error: CloseCircleFilled,
  warning: ExclamationCircleFilled
};
const iconMapOutlined = {
  success: CheckCircleOutlined,
  info: InfoCircleOutlined,
  error: CloseCircleOutlined,
  warning: ExclamationCircleOutlined
};
export const Alert = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    type = "info",
    message,
    description,
    banner = false,
    icon,
    showIcon = false,
    closable = false,
    onClose,
    afterClose,
    className,
    style,
    ...rest
  } = _ref;
  const [closed, setClosed] = useState(false);
  const handleClose = event => {
    event.preventDefault();
    setClosed(true);
    onClose?.(event);
  };
  const iconType = (description ? iconMapOutlined : iconMapFilled)[type] ?? null;
  const prefixIcon = icon ? /*#__PURE__*/_jsx(PrefixIcon, {
    children: icon
  }) : showIcon ? /*#__PURE__*/_jsx(PrefixIcon, {
    as: iconType
  }) : null;
  const closeButton = closable ? /*#__PURE__*/_jsx(CloseButton, {
    type: "button",
    onClick: handleClose,
    tabIndex: 0,
    children: /*#__PURE__*/_jsx(CloseOutlined, {})
  }) : null;
  return /*#__PURE__*/_jsx(Motion, {
    visible: !closed,
    motionName: "ant-alert-motion",
    motionAppear: false,
    motionEnter: false,
    onLeaveStart: node => ({
      maxHeight: node.offsetHeight
    }),
    onLeaveEnd: afterClose,
    children: _ref2 => {
      let {
        className: motionClassName,
        style: motionStyle
      } = _ref2;
      return /*#__PURE__*/_jsxs(StyledAlert, {
        ref: ref,
        ...rest,
        className: cx(className, motionClassName),
        style: {
          ...style,
          ...motionStyle
        },
        type: type,
        message: message,
        description: description,
        banner: banner,
        showIcon: !!prefixIcon,
        closable: closable,
        role: "alert",
        children: [prefixIcon, message ? /*#__PURE__*/_jsx(Message, {
          children: message
        }) : null, description ? /*#__PURE__*/_jsx(Description, {
          children: description
        }) : null, closeButton]
      });
    }
  });
});
export default Alert;